import { useId, useState } from "react";

import type { ComposerModel } from "./provenance.constants.ts";

/**
 * The field an act needs before it can be recorded.
 *
 * Replaces three `window.prompt` calls, which could not be styled, could not
 * show the model's refusal beside the field that caused it, and are blocked
 * outright in some browsers. Shaped like `AttachOndoId`: the only state here is
 * what has been typed, and every rule is the model's — `onRecord` returns the
 * problem sentence or null, and the field stays open with the text still in it
 * when it returns one, because a refused act must not look like a sent one.
 */
export function ProvenanceActComposer({
  composer,
  onRecord,
  onCancel,
}: {
  composer: ComposerModel;
  onRecord: (value: string) => string | null;
  onCancel: () => void;
}) {
  const field = useId();
  const [value, setValue] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const record = () => {
    const failed = onRecord(value);
    setProblem(failed);
    if (!failed) onCancel();
  };

  return (
    <div className="provenance__composer">
      <label htmlFor={field}>{composer.label}</label>
      {composer.multiline ? (
        <textarea
          id={field}
          rows={3}
          value={value}
          placeholder={composer.placeholder}
          onChange={(event) => setValue(event.target.value)}
        />
      ) : (
        <input
          id={field}
          value={value}
          placeholder={composer.placeholder}
          onChange={(event) => setValue(event.target.value)}
        />
      )}

      {problem !== null && (
        <p className="provenance__problem" role="alert">
          {problem}
        </p>
      )}

      <p className="provenance__help">{composer.help}</p>

      <div className="provenance__composer-actions">
        <button type="button" className="provenance__subcard-action" onClick={record}>
          {composer.action}
        </button>
        <button type="button" className="provenance__link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
